import React, { useState, useEffect } from 'react';
import { Play, Pause, X, ChevronLeft, ChevronRight, Volume2, VolumeX, SkipBack, Maximize2, Minimize2 } from 'lucide-react';
import { useZen } from '../contexts/ZenContext';
import { Slider } from './ui/slider';

// 10 fonds nature animés (videos/gifs en boucle)
const NATURE_BACKGROUNDS = [
  {
    id: 'ocean-waves',
    name: 'Vagues de l\'océan',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-waves-in-the-water-1164-large.mp4',
    poster: 'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=400'
  },
  {
    id: 'forest-light',
    name: 'Lumière en forêt',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-sun-coming-through-the-trees-in-the-forest-34422-large.mp4',
    poster: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=400'
  },
  {
    id: 'waterfall',
    name: 'Cascade paisible',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-waterfall-in-forest-2213-large.mp4',
    poster: 'https://images.unsplash.com/photo-1432405972618-c60b0225b8f9?w=400'
  },
  {
    id: 'clouds',
    name: 'Nuages flottants',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-white-clouds-moving-in-a-blue-sky-2408-large.mp4',
    poster: 'https://images.unsplash.com/photo-1517483000871-1dbf64a6e1c6?w=400'
  },
  {
    id: 'rain-window',
    name: 'Pluie sur vitre',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-rain-falling-on-the-water-of-a-lake-seen-up-18312-large.mp4',
    poster: 'https://images.unsplash.com/photo-1515694346937-94d85e41e6f0?w=400'
  },
  {
    id: 'northern-lights',
    name: 'Aurores boréales',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-night-sky-with-stars-at-a-calm-lake-reflecting-them-33586-large.mp4',
    poster: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=400'
  },
  {
    id: 'flower-field',
    name: 'Champ de fleurs',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-meadow-of-lavender-1467-large.mp4',
    poster: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=400'
  },
  {
    id: 'mountain-mist',
    name: 'Brume montagneuse',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-clouds-and-blue-sky-2408-large.mp4',
    poster: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=400'
  },
  {
    id: 'river-stream',
    name: 'Ruisseau cristallin',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-river-beside-trees-702-large.mp4',
    poster: 'https://images.unsplash.com/photo-1433086966358-54859d0ed716?w=400'
  },
  {
    id: 'sunset-beach',
    name: 'Coucher de soleil',
    type: 'video',
    url: 'https://assets.mixkit.co/videos/preview/mixkit-sun-setting-over-the-beach-1167-large.mp4',
    poster: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400'
  }
];

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const ImmersivePlayer = ({ onClose }) => {
  const [selectedBg, setSelectedBg] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [showBgSelector, setShowBgSelector] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const controlsTimeout = React.useRef(null);

  const {
    currentTrack,
    isPlaying,
    duration,
    currentTime,
    mainVolume,
    togglePlay,
    stopTrack,
    seekTo,
    updateMainVolume
  } = useZen();

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const currentBg = NATURE_BACKGROUNDS[selectedBg];

  // Auto-hide controls
  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current);
      }
      controlsTimeout.current = setTimeout(() => {
        if (!showBgSelector) {
          setShowControls(false);
        }
      }, 3000);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current);
      }
    };
  }, [showBgSelector]);

  // Fullscreen handling
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const nextBg = () => {
    setSelectedBg((prev) => (prev + 1) % NATURE_BACKGROUNDS.length);
  };

  const prevBg = () => {
    setSelectedBg((prev) => (prev - 1 + NATURE_BACKGROUNDS.length) % NATURE_BACKGROUNDS.length);
  };

  if (!currentTrack) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-black" data-testid="immersive-player">
      {/* Video Background */}
      <div className="absolute inset-0">
        <video
          key={currentBg.id}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
          poster={currentBg.poster}
        >
          <source src={currentBg.url} type="video/mp4" />
        </video>
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40" />
      </div>

      {/* Controls Container */}
      <div 
        className={`absolute inset-0 flex flex-col justify-between transition-opacity duration-500 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Top Bar */}
        <div className="p-4 flex items-center justify-between">
          <button
            onClick={onClose}
            className="p-3 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-all"
            data-testid="close-immersive"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBgSelector(!showBgSelector)}
              className="px-4 py-2 rounded-full bg-black/40 backdrop-blur-sm text-white text-sm hover:bg-black/60 transition-all"
              data-testid="change-bg-btn"
            >
              {currentBg.name}
            </button>
            <button
              onClick={toggleFullscreen}
              className="p-3 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-all"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Center - Track Info */}
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <h2 className="text-white text-3xl sm:text-5xl font-heading font-semibold mb-2 drop-shadow-lg">
              {currentTrack.title}
            </h2>
            {currentTrack.description && (
              <p className="text-white/70 text-lg drop-shadow-md">{currentTrack.description}</p>
            )}
          </div>
        </div>

        {/* Bottom Controls */}
        <div className="p-6 space-y-4">
          {/* Progress Bar */}
          <div className="max-w-3xl mx-auto">
            <div 
              className="relative h-2 bg-white/20 rounded-full cursor-pointer overflow-hidden backdrop-blur-sm"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const percent = (e.clientX - rect.left) / rect.width;
                seekTo(percent * duration);
              }}
            >
              <div 
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-amber-400 to-cyan-400 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-sm text-white/60 font-mono">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Play Controls */}
          <div className="flex items-center justify-center gap-6">
            <button
              onClick={prevBg}
              className="p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all"
              title="Fond précédent"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <button
              onClick={() => seekTo(Math.max(0, currentTime - 10))}
              className="p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all"
              title="-10s"
            >
              <SkipBack className="w-5 h-5" />
            </button>

            <button
              onClick={togglePlay}
              className="p-6 rounded-full bg-white text-slate-900 hover:scale-105 transition-transform shadow-xl"
              data-testid="immersive-play-btn"
            >
              {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
            </button>

            <button
              onClick={() => seekTo(Math.min(duration, currentTime + 10))}
              className="p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all"
              title="+10s"
            >
              <SkipBack className="w-5 h-5 rotate-180" />
            </button>

            <button
              onClick={nextBg}
              className="p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all"
              title="Fond suivant"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Volume Control */}
          <div className="flex items-center justify-center gap-3 max-w-xs mx-auto">
            <button
              onClick={() => updateMainVolume(mainVolume === 0 ? 0.7 : 0)}
              className="text-white/70 hover:text-white transition-colors"
            >
              {mainVolume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <Slider
              value={[mainVolume * 100]}
              onValueChange={(value) => updateMainVolume(value[0] / 100)}
              max={100}
              step={1}
              className="w-32"
            />
          </div>
        </div>
      </div>

      {/* Background Selector Modal */}
      {showBgSelector && (
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-10">
          <div className="bg-slate-900/90 backdrop-blur-md rounded-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white text-xl font-heading">Choisir un fond nature</h3>
              <button
                onClick={() => setShowBgSelector(false)}
                className="text-white/60 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {NATURE_BACKGROUNDS.map((bg, index) => (
                <button
                  key={bg.id}
                  onClick={() => {
                    setSelectedBg(index);
                    setShowBgSelector(false);
                  }}
                  className={`relative aspect-video rounded-xl overflow-hidden border-2 transition-all ${
                    selectedBg === index
                      ? 'border-amber-400 ring-2 ring-amber-400/30'
                      : 'border-transparent hover:border-white/30'
                  }`}
                  data-testid={`bg-nature-${bg.id}`}
                >
                  <img
                    src={bg.poster}
                    alt={bg.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-2">
                    <span className="text-white text-xs font-medium">{bg.name}</span>
                  </div>
                  {selectedBg === index && (
                    <div className="absolute top-1 right-1 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center">
                      <span className="text-slate-900 text-xs">✓</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImmersivePlayer;
